import { AppDispatch, RootState } from "src/store/store";
import { AppRoutes, StatusCode } from "src/common/constants/constantEnums";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  EnglishLabels,
  Regisration_Form_Labels_English,
} from "src/common/constants/englishLabels";
import { GlobalUtils, toCamelCase } from "src/utils/globalUtils";
import {
  HindiLabels,
  Regisration_Form_Labels_Hindi,
} from "src/common/constants/hindiLabels";
import { HomeVertical, MksLogo } from "../resources/icons";
import { Link, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AuthService } from "src/rest/authService";
import { GlobalLangSelector } from "src/common/globalLangSelector";
import { MobileNumberInput } from "src/common/mobileNumberInput";
import { PasswordInput } from "src/common/passwordInput";
import { UseAppToast } from "src/common/useAppToast";

export const RegisterPage: React.FC = () => {
  const globalInfo = useSelector((state: RootState) => state.globalInfo);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { errorToast } = UseAppToast();
  const { globalLanguage } = globalInfo;
  const isHindiLabels = globalLanguage === "HI";

  const [isPasswordStep, setIsPasswordStep] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPasswordTouched, setIsPasswordTouched] = useState(false);
  const [isConfirmPasswordTouched, setIsConfirmPasswordTouched] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [emailId, setEmailId] = useState("");
  const [isEmailInValid, setIsEmailInValid] = useState(false);
  const role = "USER";

  const isPasswordValid = GlobalUtils.isValidPassword(password);
  const isConfirmPasswordValid = GlobalUtils.isValidPassword(confirmPassword) && confirmPassword === password;

  useEffect(() => {
    GlobalUtils.setGlobalLanguage(dispatch);
    GlobalUtils.clearSession();
  }, [dispatch]);
  
  const register = async (): Promise<void> => {
    if (!GlobalUtils.isValidPassword(password)) {
      errorToast(
        isHindiLabels ? HindiLabels.PASSWORD_REQUIREMENTS_HINT : EnglishLabels.PASSWORD_REQUIREMENTS_HINT
      );
      return;
    }

    if (password !== confirmPassword) {
      errorToast(
        isHindiLabels ? "पासवर्ड मेल नहीं खाते" : "Passwords do not match!"
      );
      return;
    }

    try {
      const response = await AuthService.register(emailId, mobileNumber, password, role);
      if (response.status === StatusCode.CREATED_STATUS_CODE) {
        GlobalUtils.setToken(response.data.token);
        GlobalUtils.setUserLoginInfo(
          response.data.email,
          response.data.mobileNo
        );
        navigate(AppRoutes.REGISTRATION_FORM, { state: { fromApp: true } });
      }
    }
     catch (error: any) {
      if (error.response && error.response.data?.message) {
        errorToast(error.response.data.message);
      } else {
        errorToast(isHindiLabels ? "कुछ गलत हो गया" : "Something went wrong.");
     } 
    } 
  };

  
  const validateEmail = (): void => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    setIsEmailInValid(!emailRegex.test(emailId));
  };

  
  return (
    <Grid templateColumns="repeat(6, 1fr)" gap="2rem" height="100vh">
      <GridItem colSpan={{ lg: 4 }} hideBelow="lg">
        <HomeVertical />
      </GridItem>

      <GridItem colSpan={{ sm: 6, lg: 2 }}>
        <VStack m="2rem" spacing="1rem">
          {/* Header */}
          <Flex
            height="5rem"
            width="100%"
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box alignSelf="flex-start" height="4rem" width="4rem">
              <MksLogo />
            </Box>

            <Flex justifyContent="flex-end" width="calc(100%-4rem)">
              <GlobalLangSelector />
              <Link to={AppRoutes.LOGIN} state={{ fromApp: true }}>
                <Button ml="0.5rem" variant="mks">
                  <Heading color="white" fontSize="large">
                    {isHindiLabels ? HindiLabels.Login : EnglishLabels.Login}
                  </Heading>
                </Button>
              </Link>
            </Flex>
          </Flex>

          {/* Form */}
          <VStack width={{ sm: "100%", md: "65%", lg: "100%" }} spacing="1.5rem">
            <Flex width="100%" direction="column" justifyContent="flex-start">
              <Heading fontSize="70">
                {isHindiLabels ? HindiLabels.Register : EnglishLabels.Register}
              </Heading>
            </Flex>

            <VStack alignItems="center" spacing="1rem" width="100%">
              {!isPasswordStep ? (
                <>
                  {/* Email Input */}
                  <VStack width="100%" alignItems="start">
                    <Input
                      isInvalid={isEmailInValid}
                      textColor={isEmailInValid ? "#E53E3E" : "black"}
                      onBlur={validateEmail}
                      background="rgba(108, 117, 125, 0.08)"
                      borderColor="transparent"
                      className="form-input"
                      onChange={(e) => {
                        setEmailId(e.target.value);
                        isEmailInValid && setIsEmailInValid(false);
                      }}
                      placeholder={
                        isHindiLabels
                          ? HindiLabels.Enter_Email
                          : EnglishLabels.Enter_Email
                      }
                    />
                    {isEmailInValid && (
                      <Text color="#E53E3E">
                        {isHindiLabels
                          ? `${Regisration_Form_Labels_Hindi.EMAIL_ID} अमान्य`
                          : `Invalid ${toCamelCase(Regisration_Form_Labels_English.EMAIL_ID)}`}
                      </Text>
                    )}
                  </VStack>

                  {/* Mobile Input */}
                  <MobileNumberInput
                    mobileNumber={mobileNumber}
                    setMobileNumber={setMobileNumber}
                  />
                </>
              ) : (
                <>
                  {/* Password Inputs */}
                  <VStack width="100%" alignItems="start" spacing="0.25rem">
                    <PasswordInput
                      setPassword={setPassword}
                      isInvalid={isPasswordTouched && !isPasswordValid}
                      onBlur={() => setIsPasswordTouched(true)}
                      placeholder={
                        isHindiLabels
                          ? HindiLabels.Enter_Password
                          : EnglishLabels.Enter_Password
                      }
                    />
                    {isPasswordTouched && !isPasswordValid && (
                      <Text color="#E53E3E" fontSize="sm">
                        {isHindiLabels
                          ? HindiLabels.PASSWORD_REQUIREMENTS_HINT
                          : EnglishLabels.PASSWORD_REQUIREMENTS_HINT}
                      </Text>
                    )}
                  </VStack>
                  <VStack width="100%" alignItems="start" spacing="0.25rem">
                    <PasswordInput
                      setPassword={setConfirmPassword}
                      isInvalid={isConfirmPasswordTouched && !isConfirmPasswordValid}
                      onBlur={() => setIsConfirmPasswordTouched(true)}
                      placeholder={
                        isHindiLabels
                          ? HindiLabels.Confirm_Password
                          : EnglishLabels.Confirm_Password
                      }
                    />
                    {isConfirmPasswordTouched && !isConfirmPasswordValid && (
                      <Text color="#E53E3E" fontSize="sm">
                        {confirmPassword !== password
                          ? (isHindiLabels ? "पासवर्ड मेल नहीं खाते" : "Passwords do not match!")
                          : (isHindiLabels
                              ? HindiLabels.PASSWORD_REQUIREMENTS_HINT
                              : EnglishLabels.PASSWORD_REQUIREMENTS_HINT)}
                      </Text>
                    )}
                  </VStack>
                </>
              )}
            </VStack>

            {/* Action Buttons */}
            {isPasswordStep ? (
              <Button
                isDisabled={!isPasswordValid || !isConfirmPasswordValid}
                _hover={{ bgColor: "#7c4a46" }}
                width="100%"
                borderRadius="0.5rem"
                background="#4A2C2A"
                alignItems="center"
                onClick={register}
              >
                <Heading color="white" fontSize="large">
                  {isHindiLabels
                    ? HindiLabels.Register
                    : EnglishLabels.Register}
                </Heading>
              </Button>
            ) : (
              <Button
                isDisabled={
                  isEmailInValid || !emailId || !mobileNumber || mobileNumber.length !== 10
                }
                _hover={{ bgColor: "#7c4a46" }}
                width="100%"
                borderRadius="0.5rem"
                background="#4A2C2A"
                alignItems="center"
                onClick={() => setIsPasswordStep(true)}
              >
                <Heading color="white" fontSize="large">
                  {isHindiLabels ? HindiLabels.Proceed : EnglishLabels.Proceed}
                </Heading>
              </Button>
            )}

            <Button
              width="100%"
              border="1px solid #4A2C2A"
              borderRadius="0.5rem"
              background="white"
              alignItems="center"
              as={Link}
              to="/"
            >
              <Heading
                color="#4A2C2A"
                fontSize="large"
                fontFamily="PP Neue Montreal"
              >
                {isHindiLabels
                  ? HindiLabels.Back_to_Home
                  : EnglishLabels.Back_to_Home}
              </Heading>
            </Button>

            <VStack alignItems="center">
              <Text>
                {isHindiLabels
                  ? HindiLabels.Already_Have_Account
                  : EnglishLabels.Already_Have_Account}
                <Link to={AppRoutes.LOGIN}>
                  <Text
                    ml="0.5rem"
                    display="inline"
                    _hover={{ textColor: "#FF7E6B", textDecoration: "underline" }}
                  >
                    {isHindiLabels ? HindiLabels.Login : EnglishLabels.Login}
                  </Text>
                </Link>
              </Text>
            </VStack>
          </VStack>
        </VStack>
      </GridItem>
    </Grid>
  );
};
